import type {ComparisonView} from "../../../clients/Purchaser/Compare.ts";
import {twMerge} from "tailwind-merge";
import {useContext, useState} from "react";
import {LayoutContext} from "../../../context/LayoutContext.tsx";
import {CheckCircle, ChevronDown, ChevronUp} from "lucide-react";

export default function ProgressBar({comparison}: {comparison: ComparisonView}) {
    const layout = useContext(LayoutContext);

    const [expanded, setExpanded] = useState(false);

    const completedBlocks = comparison.blocks.filter(block => !!block.content);
    const completionPercentage = Math.round(completedBlocks.length / comparison.blocks.length * 100);
    const isFullyLoaded = comparison.blocks.every(block => !!block.content);

    const estimatedCompletionTime = comparison.secondsRemaining ? Date.now() + (comparison.secondsRemaining * 1000) : null;

    return (
        <div>
            {/* Progress indicator while blocks are still being generated */}
            {!isFullyLoaded &&
              <div className={twMerge(
                  "fixed bottom-10 right-[300px] flex items-center justify-center pointer-events-none",
                  layout.isMenuExpanded ? "left-48" : "-left-4"
              )}>
                  <div className="bg-black rounded w-96 p-3 text-white pointer-events-auto">
                      <div className="flex gap-4 items-center">
                          <div
                            className="rounded-full w-10 h-10 flex items-center justify-center text-xs font-normal"
                            style={{background: `radial-gradient(closest-side, black 89%, transparent 90% 100%), conic-gradient(#A76502 ${completionPercentage}%, #44403C 0)`}}
                          >
                              {completedBlocks.length}/{comparison.blocks.length}
                          </div>
                          <div className="flex-1">
                              <div>Generating Your Bid Comparison</div>
                              <div className="text-stone-400 text-xs">
                                  {estimatedCompletionTime ?
                                      <span>Estimated to be ready at {new Date(estimatedCompletionTime).toLocaleTimeString("en-US", {minute: "2-digit", hour: "numeric"})}</span>
                                      :
                                      <span>This will take a few minutes.</span>
                                  }
                              </div>
                          </div>
                          <button>
                              {expanded ?
                                  <ChevronDown onClick={() => setExpanded(false)} className="w-8 h-8 stroke-stone-400 stroke-1" />
                                  :
                                  <ChevronUp onClick={() => setExpanded(true)} className="w-8 h-8 stroke-stone-400 stroke-1" />
                              }
                          </button>
                      </div>
                      {expanded && (
                          <div className="mt-3 pt-3 border-t border-stone-700 flex flex-col gap-y-3">
                              {comparison.blocks.map(block => (
                                  <div key={block.id} className="flex items-center gap-2">
                                      {/* When we have block content, it's a green checkmark */}
                                      {block.content && <CheckCircle className="h-4 w-4 stroke-green-600" />}
                                      {/* When some file level blocks have content, it's a gray spinner */}
                                      {!block.content && block.fileBlocks.some(fb => fb.content) && <div className="h-4 w-4 rounded-full border-2 border-dashed border-stone-400 animate-spin" />}
                                      <span className="text-xs flex-1">{block.type}</span>
                                      <span className="text-stone-400 text-xs">
                                          {/* Percentage completion is just the percentage of file blocks with content. */}
                                          {block.content && "100%"}
                                          {/* In progress */}
                                          {!block.content && block.fileBlocks.filter(fb => !!fb.content).length > 0 &&
                                              // the percentage is multiplied by 90 to account for the final aggregation step.  otherwise it would hang at 100% during aggregation.
                                              Math.round(block.fileBlocks.filter(fb => !!fb.content).length / block.fileBlocks.length * 90) + "%"
                                          }
                                          {/* Unstarted */}
                                          {!block.fileBlocks.filter(fb => !!fb.content).length && "-"}
                                      </span>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              </div>
            }
        </div>
    );
}